'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { KPICardSkeleton } from '@/components/ui/LoadingSkeleton';
import { createClient } from '@/lib/supabase/client';

const LeadTrendChart = dynamic(() => import('./LeadTrendChart'), { ssr: false });

interface Lead {
  id: string;
  name: string;
  status: string;
  source: string | null;
  property_interest: string | null;
  created_at: string;
  agent_id: string | null;
}

const staticKpiData = [
  { id: 'kpi-closing', label: 'Closing', value: 3, unit: 'transaksi', trend: '+1', trendUp: true, period: 'bulan ini', icon: 'TrophyIcon', color: 'text-accent', bg: 'bg-accent/20' },
  { id: 'kpi-btp', label: 'BTP Wallet', value: '2.450', unit: 'BTP', trend: '+310', trendUp: true, period: 'bulan ini', icon: 'StarIcon', color: 'text-accent', bg: 'bg-accent/20' },
  { id: 'kpi-commission', label: 'Komisi Pending', value: 'Rp 4,5 Jt', unit: '', trend: 'Belum cair', trendUp: false, period: 'menunggu approval', icon: 'BanknotesIcon', color: 'text-orange', bg: 'bg-orange/10' },
];

const activityFeed = [
  { id: 'act-001', type: 'btp', message: '+10 BTP diterima', detail: 'Lead Dewi Rahayu tervalidasi', time: '2 jam lalu', icon: 'StarIcon' },
  { id: 'act-002', type: 'lead', message: 'Lead baru masuk', detail: 'Budi Santoso tertarik Kavling A-12', time: '10 menit lalu', icon: 'UserPlusIcon' },
  { id: 'act-003', type: 'commission', message: 'Komisi disetujui', detail: 'Rp 1.500.000 untuk closing Kavling G-11', time: 'Kemarin', icon: 'BanknotesIcon' },
  { id: 'act-004', type: 'share', message: 'Link dibagikan', detail: 'Kavling E-22 via WhatsApp', time: '3 hari lalu', icon: 'ShareIcon' },
];

const kavlingToShare = [
  { id: 'share-001', name: 'Kavling A-12', project: 'Green Valley', price: 'Rp 180 Jt', area: '120 m²', status: 'AVAILABLE' as const },
  { id: 'share-002', name: 'Kavling C-03', project: 'Emerald Hills', price: 'Rp 360 Jt', area: '200 m²', status: 'AVAILABLE' as const },
  { id: 'share-003', name: 'Kavling E-22', project: 'Puncak Asri', price: 'Rp 600 Jt', area: '300 m²', status: 'AVAILABLE' as const },
];

const VALID_STATUSES = ['Contacted', 'Survey', 'Booking', 'Closed'];

export default function AgentDashboardContent() {
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [shareCenterOpen, setShareCenterOpen] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const supabase = createClient();

  useEffect(() => {
    const fetchLeads = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('leads')
        .select('id, name, status, source, property_interest, created_at, agent_id')
        .order('created_at', { ascending: false });
      if (!error && data) setLeads(data as Lead[]);
      setIsLoading(false);
    };

    fetchLeads();

    const channel = supabase
      .channel('agent_dashboard_leads')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leads' }, (payload) => {
        setLeads((prev) => [payload.new as Lead, ...prev]);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads' }, (payload) => {
        setLeads((prev) =>
          prev.map((l) => l.id === payload.new.id ? { ...l, ...(payload.new as Lead) } : l)
        );
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'leads' }, (payload) => {
        setLeads((prev) => prev.filter((l) => l.id !== payload.old.id));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const totalLeads = leads.length;
  const validLeads = leads.filter((l) => VALID_STATUSES.includes(l.status)).length;

  const kpiData = [
    { id: 'kpi-total-lead', label: 'Total Lead', value: totalLeads, unit: 'lead', trend: `${totalLeads}`, trendUp: true, period: 'semua waktu', icon: 'UserGroupIcon', color: 'text-blue', bg: 'bg-blue/10' },
    { id: 'kpi-valid-lead', label: 'Valid Lead', value: validLeads, unit: 'lead', trend: `${validLeads}`, trendUp: true, period: 'semua waktu', icon: 'CheckBadgeIcon', color: 'text-secondary', bg: 'bg-secondary/10' },
    ...staticKpiData,
  ];

  const recentLeads = leads.slice(0, 5).map((l) => ({
    id: l.id,
    name: l.name,
    property: l.property_interest || 'Kavling',
    project: '—',
    status: (l.status || 'New') as 'New' | 'Contacted' | 'Survey' | 'Booking' | 'Closed',
    source: l.source || 'Website',
    time: (() => {
      const diff = Date.now() - new Date(l.created_at).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins} menit lalu`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs} jam lalu`;
      return `${Math.floor(hrs / 24)} hari lalu`;
    })(),
  }));

  const handleCopyLink = (kavlingId: string) => {
    navigator.clipboard.writeText(`https://betaland.id/kavling/${kavlingId}?ref=AGT-042`);
    setCopiedId(kavlingId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 max-w-screen-2xl mx-auto">
      {/* KPI Cards — horizontal scroll on mobile */}
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 mb-6 -mx-4 px-4 lg:mx-0 lg:px-0 lg:grid lg:grid-cols-5 lg:overflow-visible">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => <div key={`kpi-skel-${i}`} className="flex-shrink-0 w-44 lg:w-auto"><KPICardSkeleton /></div>)
          : kpiData.map((kpi) => (
            <div
              key={kpi.id}
              className="flex-shrink-0 w-44 lg:w-auto bg-card rounded-xl border border-border p-4 lg:p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                  <Icon name={kpi.icon as any} size={18} className={kpi.color} />
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${kpi.trendUp ? 'bg-secondary/10 text-secondary' : 'bg-orange/10 text-orange'}`}>
                  {kpi.trend}
                </span>
              </div>
              <p className="text-2xl font-bold text-foreground font-tabular mb-0.5">{kpi.value}</p>
              <p className="text-xs font-semibold text-muted-foreground">{kpi.label}</p>
              {kpi.unit && <p className="text-xs text-muted-foreground">{kpi.unit}</p>}
              <p className="text-xs text-muted-foreground mt-1">{kpi.period}</p>
            </div>
          ))
        }
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
        {/* Left column: Chart + Recent Leads */}
        <div className="lg:col-span-2 xl:col-span-3 flex flex-col gap-4">
          {/* Target progress */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground">Progress Target Agustus 2026</h2>
              <span className="text-xs text-muted-foreground">20 Agustus 2026</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Target Lead</span>
                  <span className="text-sm font-bold text-primary">{totalLeads} / 60</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div className="h-3 rounded-full bg-primary transition-all duration-500" style={{ width: `${Math.min((totalLeads / 60) * 100, 100)}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{Math.round((totalLeads / 60) * 100)}% tercapai · {Math.max(60 - totalLeads, 0)} lead lagi</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Target Closing</span>
                  <span className="text-sm font-bold text-accent">3 / 5</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div className="h-3 rounded-full gradient-gold transition-all duration-500" style={{ width: '60%' }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">60% tercapai · 2 closing lagi</p>
              </div>
            </div>
          </div>

          {/* Lead trend chart */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-bold text-foreground">Tren Lead 30 Hari</h2>
                <p className="text-xs text-muted-foreground">Total lead masuk vs valid lead</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded bg-primary inline-block" />Total Lead</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded bg-secondary inline-block" />Valid Lead</span>
              </div>
            </div>
            <LeadTrendChart />
          </div>

          {/* Recent Leads */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-foreground">Lead Terbaru</h2>
                <div className="flex items-center gap-1.5 text-xs text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                  Live
                </div>
              </div>
              <a href="/agent-crm" className="text-sm font-semibold text-primary hover:underline">Lihat Semua →</a>
            </div>
            <div className="flex flex-col gap-2">
              {recentLeads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Belum ada lead</p>
              ) : recentLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground truncate">{lead.name}</p>
                      <StatusBadge status={lead.status} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{lead.property} · {lead.project}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground">{lead.source}</p>
                    <p className="text-xs text-muted-foreground">{lead.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: BTP Wallet + Share Center + Activity */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          {/* BTP Wallet */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="gradient-primary p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold opacity-80 uppercase tracking-wide">BTP Wallet</p>
                <Icon name="StarIcon" size={18} className="text-accent" />
              </div>
              <p className="text-3xl font-bold font-tabular">2.450</p>
              <p className="text-sm opacity-80 mt-0.5">BTP Points</p>
              <p className="text-xs opacity-60 mt-2">≈ Rp 245.000 nilai reward</p>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-foreground">Menuju Reward Rp 100.000</span>
                <span className="text-xs font-bold text-accent">750/1.000 BTP</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden mb-1">
                <div className="h-2.5 rounded-full gradient-gold" style={{ width: '75%' }} />
              </div>
              <p className="text-xs text-muted-foreground">250 BTP lagi menuju reward berikutnya</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-secondary/10 rounded-lg p-2.5 text-center">
                  <p className="text-xs text-muted-foreground">Earned</p>
                  <p className="text-sm font-bold text-secondary font-tabular">+310</p>
                </div>
                <div className="bg-orange/10 rounded-lg p-2.5 text-center">
                  <p className="text-xs text-muted-foreground">Redeemed</p>
                  <p className="text-sm font-bold text-orange font-tabular">-50</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Share Center */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-foreground">Quick Share</h2>
              <button
                onClick={() => setShareCenterOpen(!shareCenterOpen)}
                className="text-xs font-semibold text-primary"
              >
                {shareCenterOpen ? 'Tutup' : 'Lihat Semua'}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {kavlingToShare.slice(0, shareCenterOpen ? 3 : 2).map((kav) => (
                <div key={kav.id} className="border border-border rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-xs font-bold text-foreground">{kav.name}</p>
                      <p className="text-xs text-muted-foreground">{kav.project} · {kav.area}</p>
                      <p className="text-xs font-semibold text-primary">{kav.price}</p>
                    </div>
                    <StatusBadge status={kav.status} size="sm" />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyLink(kav.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        copiedId === kav.id
                          ? 'bg-secondary/10 text-secondary' : 'bg-muted text-foreground hover:bg-primary/10 hover:text-primary'
                      }`}
                    >
                      <Icon name={copiedId === kav.id ? 'CheckIcon' : 'LinkIcon'} size={13} />
                      {copiedId === kav.id ? 'Tersalin!' : 'Salin Link'}
                    </button>
                    <a
                      href={`https://wa.me/?text=Kavling+tersedia+${kav.name}+${kav.price}+https://betaland.id/kavling/${kav.id}?ref=AGT-042`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-secondary/10 text-secondary rounded-lg text-xs font-semibold hover:bg-secondary/20 transition-colors"
                    >
                      <Icon name="ChatBubbleLeftRightIcon" size={13} />
                      WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-muted rounded-xl">
              <p className="text-xs font-semibold text-foreground mb-1">Referral Link Saya</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-muted-foreground truncate">betaland.id?ref=AGT-042</code>
                <button
                  onClick={() => handleCopyLink('all')}
                  className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  aria-label="Salin referral link"
                >
                  <Icon name="ClipboardDocumentIcon" size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h2 className="text-sm font-bold text-foreground mb-4">Aktivitas Terbaru</h2>
            <div className="flex flex-col gap-3">
              {activityFeed.map((act) => (
                <div key={act.id} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    act.type === 'btp' ? 'bg-accent/20' :
                    act.type === 'commission' ? 'bg-secondary/10' :
                    act.type === 'lead' ? 'bg-blue/10' : 'bg-muted'
                  }`}>
                    <Icon name={act.icon as any} size={15} className={
                      act.type === 'btp' ? 'text-accent' :
                      act.type === 'commission' ? 'text-secondary' :
                      act.type === 'lead' ? 'text-blue' : 'text-muted-foreground'
                    } />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{act.message}</p>
                    <p className="text-xs text-muted-foreground truncate">{act.detail}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{act.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}